import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { func, bool, string, number, object } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Cookie from 'services/Cookie';
import SignatureDrawPad from './SignatureDrawPad';
import {
  useGetSavedSignaturesQuery,
  useCreateSavedSignatureMutation,
  useDeleteSavedSignatureMutation,
} from 'store/app/entities/OmmnioSignSlice';
import { I18n } from 'utils/i18n';
import { hasWebViewSession } from 'utils/webViewSession';

// Font loading cache - store loaded fonts to avoid reloading
const fontLoadCache = new Map();

// Font loading utility - ensure font is loaded before use
const ensureFontLoaded = async fontFamily => {
  // Check if we've already loaded this font
  if (fontLoadCache.has(fontFamily)) {
    return fontLoadCache.get(fontFamily);
  }

  const loadPromise = (async () => {
    try {
      // Use FontFace API to explicitly load the font
      if (!window.FontFace || !document.fonts) {
        throw new Error('FontFace API not available');
      }

      // Load regular weight font - try WOFF2 first, fallback to WOFF
      let regularFont;
      try {
        regularFont = new FontFace(
          fontFamily,
          "url('/fonts/qwitcher-grypen-latin-400-normal.woff2')",
        );
        await regularFont.load();
        document.fonts.add(regularFont);
      } catch {
        // If WOFF2 fails, try WOFF
        regularFont = new FontFace(
          fontFamily,
          "url('/fonts/qwitcher-grypen-latin-400-normal.woff')",
        );
        await regularFont.load();
        document.fonts.add(regularFont);
      }
    } catch (err) {
      console.warn('Font loading failed:', err);
      // On error, still mark as cached so we don't retry infinitely
    }
  })();

  fontLoadCache.set(fontFamily, loadPromise);
  return loadPromise;
};

// Component to render typed signature text as an image
const TypedSignatureImage = ({ text, width = 200, height = 80, className = '', sx = null }) => {
  const [imageData, setImageData] = useState(null);

  // Setup high-DPI canvas for crisp rendering on Retina displays
  const setupHiDPICanvas = useCallback((canvas, cssWidth, cssHeight) => {
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS pixels
    return ctx;
  }, []);

  // Render typed signature to canvas
  useEffect(() => {
    if (!text) {
      setImageData(null);
      return;
    }

    const fontFamily = 'Qwitcher Grypen';

    // Wait for font to load before rendering
    const renderSignature = async () => {
      await ensureFontLoaded(fontFamily);

      const canvas = document.createElement('canvas');
      const ctx = setupHiDPICanvas(canvas, width, height);

      // Font styling
      const color = '#333';
      const padding = 4;

      // Calculate available space for text (in CSS pixels)
      const maxWidth = width - padding * 2;
      const maxHeight = height - padding * 2;

      // Binary search for optimal font size that fits the text
      let minFontSize = 10;
      let maxFontSize = Math.min(maxHeight, 100);
      let optimalFontSize = minFontSize;

      // Binary search to find the largest font size that fits
      while (maxFontSize - minFontSize > 0.5) {
        const testSize = (minFontSize + maxFontSize) / 2;
        ctx.font = `${testSize}px "${fontFamily}"`;
        const metrics = ctx.measureText(text);
        const testWidth = metrics.width;

        if (testWidth <= maxWidth) {
          optimalFontSize = testSize;
          minFontSize = testSize;
        } else {
          maxFontSize = testSize;
        }
      }

      // Ensure font size doesn't exceed available height
      optimalFontSize = Math.min(optimalFontSize, maxHeight);

      // Set final font and draw text
      ctx.font = `${optimalFontSize}px "${fontFamily}"`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Draw text centered on canvas
      ctx.fillText(text, width / 2, height / 2);

      setImageData(canvas.toDataURL('image/png'));
    };

    renderSignature();
  }, [text, width, height, setupHiDPICanvas]);

  if (!imageData) {
    return null;
  }

  return (
    <Box
      component="img"
      src={imageData}
      alt={text}
      className={className}
      sx={[{ width: '100%', height: '100%', objectFit: 'contain' }, ...(sx ? [sx] : [])]}
    />
  );
};

TypedSignatureImage.propTypes = {
  text: string.isRequired,
  width: number,
  height: number,
  className: string,
  sx: object,
};

const SignatureDialog = ({ open, fieldId = null, onClose, onSave }) => {
  const [tab, setTab] = useState(null); // null until signatures are loaded
  const [drawnSignature, setDrawnSignature] = useState(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#5568E8');
  const [selectedSavedSignature, setSelectedSavedSignature] = useState(null);
  const canvasRef = useRef();
  const fileInputRef = useRef();

  const colors = ['#5568E8', '#E85555', '#333333'];
  const hasSession = !!Cookie.get('token') || hasWebViewSession();
  const canPersistSignatures = hasSession;

  // Fetch saved signatures from the API (cached in Redux)
  const { data: savedSignaturesData, isLoading: isLoadingSignatures } = useGetSavedSignaturesQuery(
    undefined,
    {
      skip: !open || !canPersistSignatures,
    },
  );

  const savedSignatures = useMemo(
    () => savedSignaturesData?.data?.signatures || [],
    [savedSignaturesData?.data?.signatures],
  );

  const [createSavedSignature] = useCreateSavedSignatureMutation();
  const [deleteSavedSignature] = useDeleteSavedSignatureMutation();

  // Set default tab only once when dialog opens and we know if there are signatures
  useEffect(() => {
    if (open && tab === null && !isLoadingSignatures) {
      if (savedSignatures.length > 0) {
        setTab(0);
        setSelectedSavedSignature(savedSignatures[0]);
      } else {
        setTab(2); // Default to Draw if no saved signatures
      }
    }
  }, [open, tab, isLoadingSignatures, savedSignatures]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTab(null);
      setDrawnSignature(null);
      setTypedSignature('');
      setUploadedSignature(null);
      setSelectedSavedSignature(null);
      if (canvasRef.current) {
        canvasRef.current.clear();
      }
    }
  }, [open]);

  const handleDeleteSavedSignature = useCallback(
    async (e, signatureId) => {
      if (!canPersistSignatures) return;
      e.stopPropagation(); // Prevent selecting the signature when clicking delete
      try {
        await deleteSavedSignature({ signatureId }).unwrap();
        // If we deleted the selected signature, clear the selection
        if (selectedSavedSignature?.id === signatureId) {
          setSelectedSavedSignature(null);
        }
      } catch (error) {
        console.error('Failed to delete signature:', error);
      }
    },
    [canPersistSignatures, deleteSavedSignature, selectedSavedSignature],
  );

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleClearCanvas = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setDrawnSignature(null);
    }
  };

  const handleSaveDrawnSignature = dataUrl => {
    setDrawnSignature(dataUrl);
  };

  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        setUploadedSignature(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Setup high-DPI canvas for crisp rendering on Retina displays
  const setupHiDPICanvas = useCallback((canvas, cssWidth, cssHeight) => {
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS pixels
    return ctx;
  }, []);

  // Render typed signature to canvas and return as data URL
  const renderTypedSignatureToCanvas = useCallback(
    async (text, options = {}) => {
      const fontFamily = options.fontFamily || 'Qwitcher Grypen';

      // Wait for font to load before rendering
      await ensureFontLoaded(fontFamily);

      const canvas = document.createElement('canvas');

      // Fixed canvas dimensions (default signature field size or custom)
      const cssWidth = options.width || 400;
      const cssHeight = options.height || 100;

      // Setup high-DPI canvas for crisp rendering
      const ctx = setupHiDPICanvas(canvas, cssWidth, cssHeight);

      // Font styling
      const color = options.color || '#333';
      const padding = options.padding || 4; // Padding on all sides

      // Calculate available space for text (in CSS pixels)
      const maxWidth = cssWidth - padding * 2;
      const maxHeight = cssHeight - padding * 2;

      // Binary search for optimal font size that fits the text
      let minFontSize = 10;
      let maxFontSize = Math.min(maxHeight, 100); // Don't exceed available height
      let optimalFontSize = minFontSize;

      // Binary search to find the largest font size that fits
      while (maxFontSize - minFontSize > 0.5) {
        const testSize = (minFontSize + maxFontSize) / 2;
        ctx.font = `${testSize}px "${fontFamily}"`;
        const metrics = ctx.measureText(text);
        const testWidth = metrics.width;

        if (testWidth <= maxWidth) {
          // Text fits horizontally, try larger
          optimalFontSize = testSize;
          minFontSize = testSize;
        } else {
          // Text too wide, try smaller
          maxFontSize = testSize;
        }
      }

      // Ensure font size doesn't exceed available height
      optimalFontSize = Math.min(optimalFontSize, maxHeight);

      // Set final font and draw text (all in CSS pixels due to transform)
      ctx.font = `${optimalFontSize}px "${fontFamily}"`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Draw text centered on canvas (using CSS pixel coordinates)
      ctx.fillText(text, cssWidth / 2, cssHeight / 2);

      return canvas.toDataURL('image/png');
    },
    [setupHiDPICanvas],
  );

  const handleSave = async () => {
    let signatureData = null;

    if (tab === 0 && selectedSavedSignature) {
      // Use saved signature - render typed signatures to canvas, use others as-is
      if (selectedSavedSignature.type === 'typed') {
        // Render saved typed signature text to canvas
        const dataUrl = await renderTypedSignatureToCanvas(selectedSavedSignature.data);
        signatureData = {
          type: 'typed',
          data: dataUrl, // Send as image data
        };
      } else {
        // Drawn/uploaded signatures are already image data
        signatureData = {
          type: selectedSavedSignature.type,
          data: selectedSavedSignature.data,
        };
      }
    } else if (tab === 2) {
      // For drawn signature, get the current canvas data
      if (canvasRef.current && !canvasRef.current.isEmpty()) {
        const dataUrl = canvasRef.current.toDataURL('image/png');
        signatureData = {
          type: 'drawn',
          data: dataUrl,
        };
        // Save to backend
        if (canPersistSignatures) {
          try {
            await createSavedSignature(signatureData).unwrap();
          } catch (error) {
            console.error('Failed to save signature:', error);
          }
        }
      }
    } else if (tab === 1 && typedSignature) {
      // Render typed signature to canvas for use
      const dataUrl = await renderTypedSignatureToCanvas(typedSignature);
      signatureData = {
        type: 'typed',
        data: dataUrl, // Send as image data
      };
      // Save TEXT to backend (not the image)
      if (canPersistSignatures) {
        try {
          await createSavedSignature({
            type: 'typed',
            data: typedSignature, // Save as text
          }).unwrap();
        } catch (error) {
          console.error('Failed to save signature:', error);
        }
      }
    } else if (tab === 3 && uploadedSignature) {
      signatureData = {
        type: 'uploaded',
        data: uploadedSignature,
      };
      // Save to backend
      if (canPersistSignatures) {
        try {
          await createSavedSignature(signatureData).unwrap();
        } catch (error) {
          console.error('Failed to save signature:', error);
        }
      }
    }

    if (signatureData && fieldId) {
      onSave(fieldId, signatureData);
      onClose();
    }
  };

  // Check if there's a valid signature for the current tab
  const hasValidSignature = () => {
    if (tab === 0) {
      return selectedSavedSignature !== null;
    }
    if (tab === 2) {
      return !!drawnSignature || (canvasRef.current && !canvasRef.current.isEmpty());
    }
    if (tab === 1) {
      return typedSignature && typedSignature.trim().length > 0;
    }
    if (tab === 3) {
      return uploadedSignature;
    }
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            width: '90%',
            maxHeight: '85vh',
            minHeight: 520,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Box
        sx={theme => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Tabs
          value={tab ?? false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={I18n.t('OmmnioSign.Signature.Saved')} value={0} disabled={tab === null} />
          <Tab label={I18n.t('OmmnioSign.Signature.Type')} value={1} disabled={tab === null} />
          <Tab label={I18n.t('OmmnioSign.Signature.Draw')} value={2} disabled={tab === null} />
          <Tab label={I18n.t('OmmnioSign.Signature.Upload')} value={3} disabled={tab === null} />
        </Tabs>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          overflowY: 'auto',
          flexGrow: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Loading state */}
        {tab === null && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Saved signatures tab */}
        {tab === 0 && (
          <Box>
            {isLoadingSignatures ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : savedSignatures.length === 0 ? (
              <Typography color="textSecondary" align="center">
                {I18n.t('OmmnioSign.Signature.NoSavedSignatures')}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                }}
              >
                {savedSignatures.map(sig => {
                  const isSelected = selectedSavedSignature?.id === sig.id;
                  return (
                    <Box
                      key={sig.id}
                      onClick={() => setSelectedSavedSignature(sig)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={e => e.key === 'Enter' && setSelectedSavedSignature(sig)}
                      sx={theme => ({
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: '#fff',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: isSelected ? `0 0 0 2px ${theme.palette.primary.light}` : 'none',
                        '&:hover': {
                          borderColor: 'primary.light',
                        },
                      })}
                    >
                      <IconButton
                        size="small"
                        onClick={e => handleDeleteSavedSignature(e, sig.id)}
                        aria-label={I18n.t('OmmnioSign.Signature.DeleteSignature')}
                        sx={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          p: '4px',
                          bgcolor: 'background.paper',
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {sig.type === 'typed' ? (
                        // Typed signatures are stored as text, render as image
                        <TypedSignatureImage
                          text={sig.data}
                          width={200}
                          height={80}
                          sx={{
                            width: '100%',
                            height: 80,
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        // Drawn/uploaded signatures are image data
                        <Box
                          component="img"
                          src={sig.data}
                          alt="Saved signature"
                          sx={{ width: '100%', height: 80, objectFit: 'contain', borderRadius: 1 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Type signature tab */}
        {tab === 1 && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              {I18n.t('OmmnioSign.Signature.TypeYourName')}
            </Typography>
            <TextField
              fullWidth
              value={typedSignature}
              onChange={e => setTypedSignature(e.target.value)}
              placeholder={I18n.t('OmmnioSign.Signature.TypePlaceholder')}
            />
            {typedSignature && (
              <Box
                sx={theme => ({
                  p: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  minHeight: 150,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  mt: 2,
                })}
              >
                <Box sx={{ fontFamily: 'Qwitcher Grypen', fontSize: 56, color: '#333' }}>
                  {typedSignature}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Draw signature tab */}
        {tab === 2 && (
          <Box sx={{ width: '100%' }}>
            <Box
              sx={theme => ({
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
                bgcolor: '#fafafa',
                mb: 2,
                position: 'relative',
              })}
            >
              <Box sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                <SignatureDrawPad
                  ref={canvasRef}
                  penColor={selectedColor}
                  height={300}
                  onChange={handleSaveDrawnSignature}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1000,
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                }}
              >
                {colors.map(color => (
                  <Box
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    sx={theme => ({
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor:
                        selectedColor === color ? theme.palette.primary.main : 'transparent',
                      backgroundColor: color,
                    })}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 1000,
                }}
              >
                <Button
                  onClick={handleClearCanvas}
                  variant="outlined"
                  sx={{
                    color: '#000',
                    borderColor: '#000',
                    '&:hover': {
                      borderColor: '#000',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {I18n.t('OmmnioSign.Signature.Clear')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Upload signature tab */}
        {tab === 3 && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              {I18n.t('OmmnioSign.Signature.UploadSignature')}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} fullWidth>
              {I18n.t('OmmnioSign.Signature.ChooseFile')}
            </Button>
            {uploadedSignature && (
              <Box
                sx={theme => ({
                  p: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  minHeight: 150,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  mt: 2,
                })}
              >
                <img src={uploadedSignature} alt="Uploaded signature" style={{ maxHeight: 120 }} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={theme => ({
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Button onClick={onClose}>{I18n.t('Common.Cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!hasValidSignature()}
        >
          {I18n.t('OmmnioSign.Signature.SaveAndInsert')}
        </Button>
      </Box>
    </Dialog>
  );
};

SignatureDialog.propTypes = {
  open: bool.isRequired,
  fieldId: string,
  onClose: func.isRequired,
  onSave: func.isRequired,
};

export default SignatureDialog;
